import { propagateUpdate } from '../common';
import { getProperty } from '../../../libs/properties';
import type {
    TemplateColorProperties,
    TemplateWatermarkProperties
} from '../../template/types';
import { Config } from '../../../video-data/config';
import { getAllTemplateAttributes } from '../index';

export type TemplatePropertyKeys = TemplateColorProperties | TemplateWatermarkProperties;
type TemplateInheritKeys = typeof Config.WATERMARK_INHERIT_TEMPLATE_KEY;

const THEME_REF_PREFIX = 'themeRef_';

/**
 * All of these types must exist as a property on the Template object.
 * For example, template.colors
 */
export enum ThemeRefTypes {
    COLORS = 'colors',
    FONTS = 'fonts',
    WATERMARK_SOURCES = 'watermarkSources'
}

export type ColorsThemeRef =`themeRef_${ThemeRefTypes.COLORS}_${number}`;
export type FontsThemeRef =`themeRef_${ThemeRefTypes.FONTS}_${number}`;
export type WatermarkThemeRef =`themeRef_${ThemeRefTypes.WATERMARK_SOURCES}_${number}`;

/**
 * Builds the themeRef string which is stored in video data
 */
export function createThemeRef(type: ThemeRefTypes.COLORS, index: number): ColorsThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(type: ThemeRefTypes.FONTS, index: number): FontsThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(type: ThemeRefTypes.WATERMARK_SOURCES, index: number): WatermarkThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(type: ThemeRefTypes, index: number) {
    return `${THEME_REF_PREFIX}${type}_${index}`;
}

/**
 * Returns the referenced template index for the given asset property. If the property isn't referenced, then -1 is
 * returned.
 * @param asset
 * @param propertyPath
 */
export function getTemplateIndexFromAssetProperty(asset: object, propertyPath: string): number {
    const value = getTemplateProperty(asset, propertyPath);
    if (!isTemplateReference(value)) {
        return -1;
    }
    return getTemplateIndex(value);
}

const templateReferenceRegexp = /^themeRef_([a-zA-Z]{1,})_(\d{1,})$/;

/**
 * Returns true if the given string is a valid template reference.
 * @param value
 */
export function isTemplateReference(value: any): boolean {
    if (typeof value !== 'string') {
        return false;
    }
    return templateReferenceRegexp.test(value);
}

/**
 * Get the template property value for the given object's property. If not template property exists, then the property
 * value is returns
 * @param object
 * @param propertyPath
 */
export function getTemplateProperty(object: object, propertyPath: string): any {
    const propertyTokens = propertyPath.split('.');

    // convert property path to template reference property path
    const nPropertyTokens = propertyTokens.length;
    const leafProperty = `_${propertyTokens[nPropertyTokens - 1]}Value`;
    const templatePropertyTokens = propertyTokens.slice(0, nPropertyTokens - 1).concat(leafProperty);
    const templatePropertyValue = getProperty(object, templatePropertyTokens);
    if (templatePropertyValue === undefined) {
        // Either the property is not a template property or the template setter has never been
        // called (so `_{property}Value` has not been defined yet). In this case we can just
        // return the property directly
        return getProperty(object, propertyTokens);
    }
    return templatePropertyValue;
}

/** Returns true if the given object has any properties which have a template value */
export function objectHasTemplateValues(object: object) {
    return getAllTemplateAttributes(object)
        .some(attr => propertyHasTemplateValue(object, attr));
}

/** Returns true if the given property is set to a template value */
export function propertyHasTemplateValue(object: object, propertyPath: string): boolean {
    return isTemplateReference(getTemplateProperty(object, propertyPath));
}

/**
 * Get the index of the given template reference. A valid template reference such as themeRef_colors_1 has 3 pieces:
 * the prefix "themeRef", the type "color" and index "1". This method returns the index.
 * @param templateReference
 */
export function getTemplateIndex(templateReference: string): number {
    const colorTokens = templateReference.split('_');
    if (colorTokens.length !== 3) {
        throw Error(`Invalid number of tokens. Expected 3 but got ${colorTokens.length}`);
    }
    return parseInt(colorTokens[2]);
}

export function createTemplateSetter(
    propertyKey: string,
    initialValue: any,
    validator: (v: any) => void,
    isInitialValueOverridden: WeakMap<object, boolean>,
    templateProperty: ThemeRefTypes
) {
    const customSaveProp = `_${propertyKey}CustomSave`;
    const valueProp = `_${propertyKey}Value`;

    return function set(value) {
        validator(value);
        if (!this[customSaveProp]) {
            this[customSaveProp] = () => {
                if (!isInitialValueOverridden.get(this)) {
                    return initialValue;
                }
                return this[valueProp];
            };
        }
        if (!isInitialValueOverridden.get(this) && initialValue === value) {
            this[valueProp] = value;
            return;
        }
        isInitialValueOverridden.set(this, true);
        if (value === this[valueProp]) {
            return;
        }

        if (isTemplateReference(value)) {
            const templateIndex = getTemplateIndex(value);

            if (typeof this.template === 'undefined') {
                throw Error(`Unable to get access to template from ${this.name}`);
            }

            const length = this['template'][templateProperty].length;
            if (templateIndex >= length) {
                throw Error(`Out of bounds. Trying to access ${templateProperty} at index ${templateIndex} of ${length - 1}`);
            }
        }

        this[valueProp] = value;
        propagateUpdate(this);
    };
}

export function createTemplateGetter(
    propertyKey: string,
    initialValue: any,
    themeRefType: ThemeRefTypes,
    valueKey?: TemplatePropertyKeys,
    inheritKey?: TemplateInheritKeys
) {
    const valueProp = `_${propertyKey}Value`;

    return function get() {
        // return initial value
        if (!(valueProp in this)) {
            return resolveTemplateValue(this, initialValue, themeRefType, valueKey, inheritKey);
        }

        // return stored value
        const value = this[valueProp];
        return resolveTemplateValue(this, value, themeRefType, valueKey, inheritKey);
    };
}

/**
 * This is where we look up the theme reference on the Template object directly.
 */
function resolveTemplateValue(
    object: { template?: object; name?: string; },
    value: any,
    templateProperty: ThemeRefTypes,
    valueKey?: TemplatePropertyKeys,
    inheritKey?: TemplateInheritKeys
) {
    if (isTemplateReference(value)) {
        const templateIndex = getTemplateIndex(value);
        if (!object['template']) {
            throw Error(`Unable to access template from ${object['name']}`);
        }
        if (templateIndex >= object['template'][templateProperty].length) {
            throw Error(`Out of bounds. Trying to access ${templateProperty} at index ${templateIndex} of ${object['template'][templateProperty].length - 1}`);
        }
        let propertyPath = object['template'][templateProperty][templateIndex];
        if (valueKey) {
            propertyPath = propertyPath[valueKey];
        }
        return propertyPath;
    }
    else if (value === Config.INHERIT_FROM_TEMPLATE) {
        if (!object['template']) {
            throw Error(`Unable to access template from ${object['name']}`);
        }
        if (!inheritKey) {
            throw Error(`Trying to access inherited value on template without defining the key ${object['name']}`);
        }
        if (!valueKey) {
            throw Error(`A valueKey is needed to support inherit ${object['name']}`);
        }
        return object['template'][inheritKey][valueKey];
    }
    else {
        return value;
    }
}
